import { useEffect, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AlertCircle,
  ArrowLeft,
  Download,
  File as FileIcon,
  Folder,
} from 'lucide-react';
import {
  Breadcrumb,
  Button,
  Card,
  EmptyState,
  FormField,
  IconButton,
  Input,
  Spinner,
} from '@ntconsult/aluma-react';
import {
  ShareErrorCode,
  SharedFileViewer,
  useShareDownload,
  useShareNavigation,
  type ShareBreadcrumbItem,
  type SharedFileResource,
  type SharedFolderResource,
} from '@features/shares';
import { formatFileSize } from '@common/utils/format';

const isFolderResource = (
  resource: SharedFileResource | SharedFolderResource,
): resource is SharedFolderResource =>
  (resource as SharedFolderResource).children !== undefined;

export function SharePage() {
  const { t } = useTranslation('shares');
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const nav = useShareNavigation(token);
  const handleDownload = useShareDownload(token, nav.password);

  const isPasswordIssue =
    nav.parsedError?.code === ShareErrorCode.PASSWORD_REQUIRED ||
    nav.parsedError?.code === ShareErrorCode.WRONG_PASSWORD;

  const needsLogin =
    !!nav.parsedError &&
    nav.parsedError.code === ShareErrorCode.LOGIN_REQUIRED &&
    !!token;

  useEffect(() => {
    if (needsLogin && token) {
      navigate('/login', {
        replace: true,
        state: { from: { pathname: `/share/${token}` } },
      });
    }
  }, [needsLogin, token, navigate]);

  const submitPassword = (e: FormEvent) => {
    e.preventDefault();
    nav.submitPassword();
  };

  if (nav.rootQuery.isLoading) {
    return (
      <PageShell>
        <Spinner size="lg" />
      </PageShell>
    );
  }

  if (isPasswordIssue) {
    const isWrong = nav.parsedError?.code === ShareErrorCode.WRONG_PASSWORD;
    return (
      <PageShell>
        <Card className="w-full max-w-md">
          <Card.Header
            title={t('public.protectedContent')}
            subtitle={t('public.passwordPrompt')}
          />
          <Card.Body>
            <form onSubmit={submitPassword} className="flex flex-col gap-4">
              <FormField
                label={t('public.enterPassword')}
                error={isWrong ? t('public.wrongPassword') : undefined}
              >
                <Input
                  type="password"
                  value={nav.passwordInput}
                  onChange={(e) => nav.setPasswordInput(e.target.value)}
                  autoFocus
                />
              </FormField>
              <Button
                type="submit"
                fullWidth
                loading={nav.rootQuery.isFetching}
                disabled={!nav.passwordInput}
              >
                {t('public.access')}
              </Button>
            </form>
          </Card.Body>
        </Card>
      </PageShell>
    );
  }

  if (needsLogin) {
    return null;
  }

  if (nav.parsedError && nav.parsedError.code !== ShareErrorCode.UNKNOWN) {
    return <ShareErrorState parsedCode={nav.parsedError.code} />;
  }

  if (nav.parsedError || !nav.rootQuery.data) {
    return <ShareErrorState parsedCode={ShareErrorCode.NOT_FOUND} />;
  }

  const { share, resource } = nav.rootQuery.data;
  const folderResource = isFolderResource(resource) ? resource : null;
  const fileResource = !folderResource ? (resource as SharedFileResource) : null;

  const remainingDownloads =
    share.maxDownloads !== null && share.maxDownloads !== undefined
      ? share.maxDownloads - share.downloadCount
      : null;

  if (fileResource) {
    return (
      <PageShell>
        <SharedFileViewer
          name={fileResource.name}
          size={fileResource.size}
          mimeType={fileResource.mimeType}
          streamUrl={fileResource.streamUrl}
          onDownload={() => handleDownload()}
        />
      </PageShell>
    );
  }

  if (nav.viewingFileId) {
    return (
      <div className="min-h-[100dvh] bg-surface-raised p-4" style={{ paddingTop: 'max(env(safe-area-inset-top), 1rem)', paddingBottom: 'max(env(safe-area-inset-bottom), 1rem)' }}>
        <div className="max-w-5xl mx-auto">
          <div className="mb-4">
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<ArrowLeft className="w-4 h-4" aria-hidden="true" />}
              onClick={() => nav.setViewingFileId(null)}
            >
              {t('public.back')}
            </Button>
          </div>

          {nav.fileViewQuery.isLoading ? (
            <div className="flex items-center justify-center py-24">
              <Spinner size="lg" />
            </div>
          ) : nav.fileViewQuery.data ? (
            <SharedFileViewer
              name={nav.fileViewQuery.data.file.name}
              size={nav.fileViewQuery.data.file.size}
              mimeType={nav.fileViewQuery.data.file.mimeType}
              streamUrl={nav.fileViewQuery.data.streamUrl}
              onDownload={() =>
                handleDownload(nav.fileViewQuery.data!.file.id)
              }
            />
          ) : null}
        </div>
      </div>
    );
  }

  const currentFolder: SharedFolderResource =
    nav.subfolderQuery.data ?? (folderResource as SharedFolderResource);
  const isFolderLoading = nav.breadcrumb.length > 0 && nav.subfolderQuery.isFetching;
  const isEmpty =
    !isFolderLoading &&
    currentFolder.children.length === 0 &&
    currentFolder.files.length === 0;

  return (
    <PageShell>
      <Card className="w-full max-w-2xl">
        <Card.Header
          decorator={
            <Folder
              className="w-6 h-6 text-foreground-muted"
              aria-hidden="true"
            />
          }
          title={folderResource!.name}
          subtitle={
            remainingDownloads !== null
              ? `${t('public.downloadsRemaining')}: ${remainingDownloads}`
              : undefined
          }
          action={
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Download className="w-4 h-4" aria-hidden="true" />}
              onClick={() =>
                handleDownload({
                  isFolder: true,
                  folderId:
                    nav.breadcrumb.length > 0 ? currentFolder.id : undefined,
                  fallbackName: `${currentFolder.name || folderResource!.name}.zip`,
                })
              }
            >
              {t('public.downloadFolder', { defaultValue: 'Baixar pasta' })}
            </Button>
          }
        />
        <Card.Body>
          <ShareBreadcrumb
            items={nav.breadcrumb}
            onNavigate={nav.truncateBreadcrumbTo}
          />

          {isFolderLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size="md" />
            </div>
          ) : isEmpty ? (
            <EmptyState
              align="center"
              title={t('public.emptyFolder')}
              decorator={
                <Folder
                  className="w-8 h-8 text-foreground-muted"
                  aria-hidden="true"
                />
              }
              decoratorPlacement="top"
            />
          ) : (
            <FolderContents
              folder={currentFolder}
              onOpenSubfolder={nav.pushBreadcrumb}
              onOpenFile={nav.setViewingFileId}
              onDownload={handleDownload}
              onDownloadSubfolder={(id, name) =>
                handleDownload({ folderId: id, fallbackName: `${name}.zip` })
              }
            />
          )}
        </Card.Body>
      </Card>
    </PageShell>
  );
}

function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="min-h-[100dvh] bg-surface-raised flex items-center justify-center p-4"
      style={{
        paddingTop: 'max(env(safe-area-inset-top), 1rem)',
        paddingBottom: 'max(env(safe-area-inset-bottom), 1rem)',
        paddingLeft: 'max(env(safe-area-inset-left), 1rem)',
        paddingRight: 'max(env(safe-area-inset-right), 1rem)',
      }}
    >
      {children}
    </div>
  );
}

function ShareErrorState({ parsedCode }: { parsedCode: ShareErrorCode }) {
  const { t } = useTranslation('shares');

  const messageMap: Partial<Record<ShareErrorCode, string>> = {
    [ShareErrorCode.EXPIRED]: t('public.expiredMessage'),
    [ShareErrorCode.DISABLED]: t('public.disabledMessage'),
    [ShareErrorCode.LIMIT_REACHED]: t('public.limitMessage'),
    [ShareErrorCode.NOT_FOUND]: t('public.notFoundMessage'),
    [ShareErrorCode.LOGIN_REQUIRED]: t('public.loginRequired'),
  };

  const title =
    parsedCode === ShareErrorCode.NOT_FOUND
      ? t('public.notFound')
      : t('public.unavailable');
  const description = messageMap[parsedCode] ?? t('public.notAvailable');

  return (
    <PageShell>
      <Card className="w-full max-w-md">
        <Card.Body>
          <EmptyState
            align="center"
            title={title}
            description={description}
            decorator={
              <AlertCircle
                className="w-8 h-8 text-danger"
                aria-hidden="true"
              />
            }
            decoratorPlacement="top"
          />
        </Card.Body>
      </Card>
    </PageShell>
  );
}

function ShareBreadcrumb({
  items,
  onNavigate,
}: {
  items: ShareBreadcrumbItem[];
  onNavigate: (index: number) => void;
}) {
  const { t } = useTranslation('shares');

  return (
    <Breadcrumb className="mb-4">
      <Breadcrumb.List>
        <Breadcrumb.Item>
          {items.length === 0 ? (
            <Breadcrumb.Page>{t('public.folderRoot')}</Breadcrumb.Page>
          ) : (
            <Breadcrumb.Link
              href="#"
              onClick={(e) => {
                e.preventDefault();
                onNavigate(0);
              }}
            >
              {t('public.folderRoot')}
            </Breadcrumb.Link>
          )}
        </Breadcrumb.Item>
        {items.map((item, idx) => {
          const isLast = idx === items.length - 1;
          return (
            <Breadcrumb.Item key={item.id}>
              <Breadcrumb.Separator />
              {isLast ? (
                <Breadcrumb.Page>{item.name}</Breadcrumb.Page>
              ) : (
                <Breadcrumb.Link
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    onNavigate(idx + 1);
                  }}
                >
                  {item.name}
                </Breadcrumb.Link>
              )}
            </Breadcrumb.Item>
          );
        })}
      </Breadcrumb.List>
    </Breadcrumb>
  );
}

function FolderContents({
  folder,
  onOpenSubfolder,
  onOpenFile,
  onDownload,
  onDownloadSubfolder,
}: {
  folder: SharedFolderResource;
  onOpenSubfolder: (id: string, name: string) => void;
  onOpenFile: (id: string) => void;
  onDownload: (fileId: string) => void;
  onDownloadSubfolder?: (id: string, name: string) => void;
}) {
  return (
    <ul className="divide-y divide-border border border-border rounded-md overflow-hidden">
      {folder.children.map((child) => (
        <li
          key={child.id}
          className="flex items-center gap-3 px-4 py-3 hover:bg-surface-raised transition-colors"
        >
          <button
            type="button"
            onClick={() => onOpenSubfolder(child.id, child.name)}
            className="flex items-center gap-3 flex-1 text-left min-w-0 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus focus-visible:ring-offset-2 rounded"
          >
            <Folder
              className="w-5 h-5 text-foreground-muted shrink-0"
              aria-hidden="true"
            />
            <span className="truncate flex-1 text-sm">{child.name}</span>
          </button>
          {onDownloadSubfolder && (
            <IconButton
              icon={<Download className="w-4 h-4" />}
              label="Download"
              variant="ghost"
              size="sm"
              onClick={() => onDownloadSubfolder(child.id, child.name)}
            />
          )}
        </li>
      ))}
      {folder.files.map((file) => (
        <li
          key={file.id}
          className="flex items-center gap-3 px-4 py-3 hover:bg-surface-raised transition-colors"
        >
          <button
            type="button"
            onClick={() => onOpenFile(file.id)}
            className="flex items-center gap-3 flex-1 text-left min-w-0 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus focus-visible:ring-offset-2 rounded"
          >
            <FileIcon
              className="w-5 h-5 text-foreground-muted shrink-0"
              aria-hidden="true"
            />
            <span className="truncate flex-1 text-sm">{file.name}</span>
            <span className="text-xs text-foreground-muted whitespace-nowrap">
              {formatFileSize(file.size)}
            </span>
          </button>
          <IconButton
            icon={<Download className="w-4 h-4" />}
            label="Download"
            variant="ghost"
            size="sm"
            onClick={() => onDownload(file.id)}
          />
        </li>
      ))}
    </ul>
  );
}
